import { FC, MouseEvent, useState } from 'react'

import { convertToPrice } from '../../../featureSliced/shared/utils/convertToPrice'
import { AllergenImages } from '../../../featureSliced/shared/utils/AllergenImages'
import { isProductProtected } from '../../../featureSliced/shared/utils/isProductProtected'
import { getProtectedAge } from '../../../featureSliced/shared/utils/getProtectedAge'

import { ReactComponent as Heart } from 'assets/hearts/full_xs.svg'
import { ReactComponent as CameraIcon } from 'assets/cameraIcon.svg'
import { ReactComponent as Squares } from 'assets/squares.svg'

import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { IOrderProduct } from '../../../sections/Order/types'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useTranslations } from '../../../featureSliced/shared/hooks/useTranslations'
import { RenderIf } from '../../RenderIf'
import { handleLocalStorage } from '../../../featureSliced/shared/utils/handleLocalStorage'
import {
  formatLeadDaysNotice,
  getItemLeadDays,
  isItemLeadDaysExceeded,
} from 'featureSliced/shared/utils/itemLeadDays'

import { IconFilled } from '../../IconsContainers/styled'
import {
  AgeProtection,
  Allergen,
  AllergensContainer,
  Discount,
  Header,
  HeaderCol,
  HeartContainer,
  PlaceholderImage,
  Price,
  ProductCounter,
  ProductImage,
  ProductImageContainer,
  ProductStyled,
  ProductTitle,
  SoldOutBanner,
  SubTemplateIcon,
  SubTemplateTitle,
} from './styled'
import { useGetTranslatedNames } from 'featureSliced/shared/hooks/useGetTranslatedNames'
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'
import { ProductTemplateIdType } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { WishlistSlice } from 'featureSliced/shared/store/reducers/WishlistReducer'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'

type ProductProps = {
  product: IProduct | IOrderProduct
  templateId: ProductTemplateIdType
  amount: number
  onProductClick?: (product: IProduct, e: MouseEvent<HTMLElement>) => void
}

export const ProductGrided: FC<ProductProps> = ({
  product,
  templateId,
  amount,
  onProductClick,
}) => {
  // Hooks
  const t = useTranslations()
  const { getProductName } = useGetTranslatedNames()
  const { getProductPrice } = useGetProductPrice()
  const dispatch = useMyDispatch()
  const addOrRemoveFromWishlist = WishlistSlice.actions.addOrRemoveFromWishlist

  // Store
  const wishlist = useMySelector((state) => state.wishlist)
  const { Currency } = useMySelector((state) => state.app.companyData)
  const { colour } = useMySelector((state) => state.app.interfaceStyles)

  // State
  const [isImgErrored, setIsImageErrored] = useState(false)

  // Functions
  const isInWishlist = (idToCheck: number) => {
    return !!wishlist?.find(
      (listItem) =>
        listItem.id === idToCheck && listItem.productTemplateId === templateId
    )
  }

  const handleWishListClick = (e: any) => {
    e.stopPropagation()
    // handleWishlist({ id: product?.id, productTemplateId: templateId })
    dispatch(
      addOrRemoveFromWishlist({
        id: product?.id,
        productTemplateId: templateId,
      })
    )
  }

  const handleProductClick = (e: MouseEvent<HTMLElement>) => {
    if (onProductClick && Boolean(!product?.soldOut)) {
      onProductClick(product, e)
    }
  }

  const prices = getProductPrice(product, templateId)
  const leadDays = getItemLeadDays((product as any)?.ClickCollectLeadDaysCount)
  const isLeadDaysExceeded = isItemLeadDaysExceeded({
    leadDays,
    pickUpTime: handleLocalStorage('pickUpTime'),
  })

  return (
    <ProductStyled onClick={handleProductClick} isSelected={!!amount}>
      <RenderIf condition={Boolean(product.innerCategoryItems?.length)}>
        <ProductImageContainer>
          <RenderIf condition={!isImgErrored}>
            <ProductImage
              src={product?.imageUrl || 'ERRORED_IMAGE'}
              onError={({ currentTarget }: any) => {
                setIsImageErrored(true)
                currentTarget.onerror = null // prevents looping
              }}
              alt="product"
            />
          </RenderIf>
          <RenderIf condition={isImgErrored}>
            <SubTemplateIcon color={colour}>
              <Squares />
            </SubTemplateIcon>
          </RenderIf>
        </ProductImageContainer>
        <SubTemplateTitle>{getProductName(product)}</SubTemplateTitle>
        {/* <SubTemplateTitle>{product?.title}</SubTemplateTitle> */}
      </RenderIf>
      <RenderIf condition={Boolean(!product.innerCategoryItems?.length)}>
        <ProductImageContainer>
          <Header>
            <HeaderCol>
              <HeartContainer
                isActive={isInWishlist(product?.id)}
                onClick={handleWishListClick}
              >
                <IconFilled color="#fff">
                  <Heart />
                </IconFilled>
              </HeartContainer>
              <RenderIf condition={isProductProtected(product)}>
                <AgeProtection>{getProtectedAge(product)}</AgeProtection>
              </RenderIf>
            </HeaderCol>
            <RenderIf condition={!!amount}>
              <ProductCounter>{amount}</ProductCounter>
            </RenderIf>
          </Header>
          <RenderIf condition={!isImgErrored}>
            <ProductImage
              src={product?.imageUrl || 'ERRORED_IMAGE'}
              // TODO: add type
              onError={({ currentTarget }: any) => {
                setIsImageErrored(true)
                currentTarget.onerror = null // prevents looping
              }}
              alt="product"
              height="57"
              width="125"
            />
          </RenderIf>
          <RenderIf condition={isImgErrored}>
            <PlaceholderImage>
              <CameraIcon />
            </PlaceholderImage>
          </RenderIf>
        </ProductImageContainer>
        <ProductTitle>{getProductName(product)}</ProductTitle>
        {/* <ProductTitle>{product?.title}</ProductTitle> */}
        <AllergensContainer>
          {product?.allergens?.map((allergen) => (
            <Allergen
              key={allergen.id}
              alt={String(allergen.id)}
              src={AllergenImages[allergen.id] || AllergenImages[0]}
            />
          ))}
        </AllergensContainer>
        {/* <Discount>{prices.discountString || ''}</Discount> */}
        <Discount>
          <RenderIf
            condition={
              Boolean(prices.discountValue) ||
              (prices.isOffer && Boolean(prices.priceOG))
            }
          >
            <span>
              {prices.priceOGString} {Currency || ''}
            </span>
          </RenderIf>
        </Discount>
        {/* product */}
        <RenderIf
          condition={
            Boolean(prices.finalPrice) &&
            Boolean(!product.innerCategoryItems?.length)
          }
        >
          <Price isSoldOut={Boolean(product?.soldOut) || isLeadDaysExceeded}>
            {convertToPrice(prices.finalPriceString)} {Currency || ''}
            <RenderIf condition={Boolean(product?.soldOut)}>
              <SoldOutBanner>{t('products.soldOut')}</SoldOutBanner>
            </RenderIf>
            <RenderIf condition={!product?.soldOut && isLeadDaysExceeded}>
              <SoldOutBanner>{formatLeadDaysNotice(t, leadDays)}</SoldOutBanner>
            </RenderIf>
          </Price>
        </RenderIf>
      </RenderIf>
    </ProductStyled>
  )
}
